import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { motion } from 'motion/react';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  CheckIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';
import { filesApi } from '../api/files';
import { useToast } from '../components/ui/Toaster';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { generateProfessionalPdf, detectDocumentType } from '../components/documents/PdfExporter';
import type { FileItem } from '../stores/fileStore';
import type { MarkdownEditorRef } from '../components/documents/MarkdownEditor';

// Lazy load the editor to reduce initial bundle size
const MarkdownEditor = lazy(() =>
  import('../components/documents/MarkdownEditor').then((m) => ({
    default: m.MarkdownEditor,
  }))
);

export function DocumentEditorPage() {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const editorRef = useRef<MarkdownEditorRef>(null);

  const [file, setFile] = useState<FileItem | null>(null);
  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Auto-save debounce timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load file and content - only depend on fileId
  useEffect(() => {
    if (!fileId) return;

    let isCancelled = false;

    const loadFile = async () => {
      setIsLoading(true);
      try {
        const fileRes = await filesApi.get(fileId);
        console.log('[DocumentEditor] File loaded:', fileRes.file.name);
        if (isCancelled) return;

        const contentRes = await filesApi.getContent(fileId);
        console.log('[DocumentEditor] Content loaded, length:', contentRes.content?.length, 'preview:', contentRes.content?.slice(0, 100));
        if (isCancelled) return;

        setFile(fileRes.file);
        setContent(contentRes.content);
        setOriginalContent(contentRes.content);
        setIsLoading(false);
      } catch (error: any) {
        if (isCancelled) return;
        console.error('[DocumentEditor] Error loading file:', error);
        // Navigate back on error - toast will show from the component
        window.history.back();
      }
    };

    loadFile();

    return () => {
      isCancelled = true;
    };
  }, [fileId]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(content !== originalContent);
  }, [content, originalContent]);

  // Warn on navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Save content - use useCallback with proper deps
  const handleSave = useCallback(async (contentToSave?: string) => {
    if (!fileId) return;

    const saveContent = contentToSave ?? content;
    if (saveContent === originalContent) return;

    setIsSaving(true);
    try {
      const response = await filesApi.updateContent(fileId, saveContent);
      setFile(response.file);
      setOriginalContent(saveContent);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error('Error saving file:', error);
      toast.error(error.message || t('documents:errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  }, [fileId, content, originalContent, toast, t]);

  // Keep a ref to handleSave so auto-save timer always calls the latest version
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // Handle content change with auto-save
  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);

    // Clear existing auto-save timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new auto-save timer (2 seconds)
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveRef.current(newContent);
    }, 2000);
  }, []);

  // Keyboard shortcut for save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSaveRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Export to PDF
  const handleExportPdf = async () => {
    if (!file) return;

    setIsExporting(true);
    try {
      // Get current locale
      const locale = i18n.language?.startsWith('es') ? 'es' : 'en';

      // Detect document type from filename
      const documentType = detectDocumentType(file.name);

      // Generate professional PDF with cover page and branding
      // Pass raw markdown content directly (not HTML)
      await generateProfessionalPdf({
        title: file.name,
        markdown: content,
        documentType,
        locale,
      });

      toast.success(t('documents:exportSuccess'));
    } catch (error: any) {
      console.error('Error exporting PDF:', error);
      toast.error(t('documents:errors.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  // Go back
  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (window.confirm(t('documents:unsavedChangesWarning'))) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!file) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          {/* Left: Back button and file name */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="font-semibold text-gray-900 dark:text-white">
                {file.name}
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                {isSaving ? (
                  <>
                    <Spinner size="sm" />
                    <span>{t('documents:saving')}</span>
                  </>
                ) : hasUnsavedChanges ? (
                  <span>{t('documents:unsavedChanges')}</span>
                ) : lastSaved ? (
                  <>
                    <CheckIcon className="h-3 w-3 text-green-500" />
                    <span>{t('documents:saved')}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleSave()}
              disabled={isSaving || !hasUnsavedChanges}
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-1" />
              {t('documents:save')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportPdf}
              disabled={isExporting}
            >
              {isExporting ? (
                <Spinner size="sm" className="mr-1" />
              ) : (
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              )}
              {t('documents:exportPdf')}
            </Button>
          </div>
        </div>
      </header>

      {/* Editor */}
      <main className="flex-1 overflow-auto p-4">
        <div className="max-w-4xl mx-auto">
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-64">
                <Spinner size="lg" />
              </div>
            }
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <MarkdownEditor
                ref={editorRef}
                content={content}
                onChange={handleContentChange}
              />
            </motion.div>
          </Suspense>
        </div>
      </main>
    </div>
  );
}
